import React, { useState, useEffect, useCallback } from 'react';
import {
    Grid,
    Paper,
    Typography,
    Box,
    Card,
    CardContent,
    CardHeader,
    Avatar,
    Chip,
    Divider,
    LinearProgress,
    CircularProgress,
    Alert,
    Button,
    IconButton,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Fade,
    Slide,
    Zoom,
    Container,
    Stack,
    useTheme,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    Dashboard as DashboardIcon,
    LocalShipping as ShippingIcon,
    People as PeopleIcon,
    AttachMoney as MoneyIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Schedule as ScheduleIcon,
    Notifications as NotificationsIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    Refresh as RefreshIcon,
    Timeline as TimelineIcon,
    LocationOn as LocationIcon,
    Speed as SpeedIcon,
    Storage as StorageIcon,
    Today as TodayIcon,
    Analytics as AnalyticsIcon,
    LocalShipping as RouteIcon,
    LocalShipping as DeliveryIcon,
    PendingActions as PendingIcon,
    Done as DoneIcon,
    AccessTime as TimeIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    Star as StarIcon,
    ArrowUpward as ArrowUpIcon,
    ArrowDownward as ArrowDownIcon,
    Remove as NoChangeIcon,
    FlightTakeoff as FlightTakeoffIcon,
    LocalShipping as LocalShippingIcon,
    FilterList as FilterIcon,
    Clear as ClearIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../hooks/useApiHandler';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import dashboardService from '../../services/dashboardService';
import authService from '../../services/authService';
import { formatAmount } from '../../utils/formatters';

// Enhanced Stat Card Component
const StatCard = ({ 
    title, 
    value, 
    icon, 
    color, 
    loading = false, 
    growth = null, 
    subtitle = null,
    onClick = null,
    gradient = false 
}) => {
    const theme = useTheme();
    
    const getGrowthIcon = () => {
        if (growth === null || growth === 0) return <NoChangeIcon sx={{ fontSize: 16 }} />;
        return growth > 0 ? <ArrowUpIcon sx={{ fontSize: 16 }} /> : <ArrowDownIcon sx={{ fontSize: 16 }} />;
    };

    const getGrowthColor = () => {
        if (growth === null || growth === 0) return 'text.secondary';
        return growth > 0 ? 'success.main' : 'error.main';
    };

    return (
        <Card 
            sx={{ 
                height: '100%',
                minHeight: { xs: 120, sm: 140, md: 160 },
                cursor: onClick ? 'pointer' : 'default',
                background: gradient 
                    ? `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`
                    : 'background.paper',
                border: `1px solid ${alpha(color, 0.2)}`,
                transition: 'all 0.3s ease',
                '&:hover': onClick ? {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                    borderColor: color
                } : {}
            }}
            onClick={onClick}
        >
            <CardContent sx={{ 
                p: { xs: 1.5, sm: 2, md: 3 },
                '&:last-child': { pb: { xs: 1.5, sm: 2, md: 3 } }
            }}>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    justifyContent: 'space-between',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 2, sm: 0 }
                }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            gutterBottom
                            sx={{ 
                                fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
                                fontWeight: 500
                            }}
                        >
                            {title}
                        </Typography>
                        <Typography 
                            variant="h4" 
                            component="div" 
                            sx={{ 
                                fontWeight: 'bold', 
                                mb: { xs: 0.5, sm: 1 },
                                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem', lg: '2.125rem' },
                                wordBreak: 'break-word'
                            }}
                        >
                            {loading ? <CircularProgress size={20} /> : value}
                        </Typography>
                        {subtitle && (
                            <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ 
                                    fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                                    display: 'block',
                                    wordBreak: 'break-word',
                                    mb: { xs: 0.5, sm: 1 }
                                }}
                            >
                                {subtitle}
                            </Typography>
                        )}
                        {growth !== null && (
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                mt: 1,
                                flexWrap: 'wrap',
                                gap: 0.5
                            }}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    color: getGrowthColor(),
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                }}>
                                    {getGrowthIcon()}
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            ml: 0.5, 
                                            fontWeight: 'medium',
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                        }}
                                    >
                                        {Math.abs(growth)}%
                                    </Typography>
                                </Box>
                                <Typography 
                                    variant="caption" 
                                    color="text.secondary" 
                                    sx={{ 
                                        ml: 1,
                                        fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                    }}
                                >
                                    vs last month
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    <Avatar
                        sx={{
                            bgcolor: `${color}20`,
                            color: color,
                            width: { xs: 40, sm: 48, md: 56 },
                            height: { xs: 40, sm: 48, md: 56 },
                            alignSelf: { xs: 'center', sm: 'flex-start' }
                        }}
                    >
                        {React.cloneElement(icon, { sx: { fontSize: { xs: 20, sm: 24, md: 28 } } })}
                    </Avatar>
                </Box>
            </CardContent>
        </Card>
    );
};







// Top Routes Widget
const TopRoutesWidget = ({ routes, loading }) => {
    const canViewRevenue = authService.canViewRevenue();
    
    return (
    <Card sx={{ height: '100%', minHeight: { xs: 250, sm: 300, md: 400 } }}>
        <CardHeader
            avatar={
                <Avatar sx={{ bgcolor: 'success.main', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                    <RouteIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
                </Avatar>
            }
            title={
                <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' } }}>
                    Top Routes
                </Typography>
            }
            subheader={
                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>
                    Most popular shipping routes
                </Typography>
            }
            sx={{ pb: 1, px: { xs: 1.5, sm: 2, md: 3 } }}
        />
        <CardContent sx={{ pt: 0, px: { xs: 1.5, sm: 2, md: 3 }, height: 'calc(100% - 80px)' }}>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : routes?.length > 0 ? (
                <TableContainer sx={{ height: '100%' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ 
                                    fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                                    py: { xs: 1, sm: 1.5 }
                                }}>
                                    Route
                                </TableCell>
                                <TableCell 
                                    align="right" 
                                    sx={{ 
                                        fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                                        display: { xs: 'none', sm: 'table-cell' },
                                        py: { xs: 1, sm: 1.5 }
                                    }}
                                >
                                    Bookings
                                </TableCell>
                                {canViewRevenue && (
                                    <TableCell 
                                        align="right" 
                                        sx={{ 
                                            fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                                            py: { xs: 1, sm: 1.5 }
                                        }}
                                    >
                                        Revenue
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {routes.map((route, index) => (
                                <TableRow key={index}>
                                    <TableCell sx={{ py: { xs: 0.75, sm: 1, md: 1.5 } }}>
                                        <Typography 
                                            variant="body2" 
                                            fontWeight="medium"
                                            sx={{ 
                                                fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                                                wordBreak: 'break-word'
                                            }}
                                        >
                                            {route.route}
                                        </Typography>
                                        <Typography 
                                            variant="caption" 
                                            color="primary"
                                            sx={{ 
                                                display: { xs: 'block', sm: 'none' },
                                                fontSize: '0.65rem'
                                            }}
                                        >
                                            {route.booking_count} bookings
                                        </Typography>
                                    </TableCell>
                                    <TableCell 
                                        align="right" 
                                        sx={{ 
                                            py: { xs: 1, sm: 1.5 },
                                            display: { xs: 'none', sm: 'table-cell' }
                                        }}
                                    >
                                        <Chip 
                                            label={route.booking_count} 
                                            size="small" 
                                            color="primary"
                                        />
                                    </TableCell>
                                    {canViewRevenue && (
                                        <TableCell align="right" sx={{ py: { xs: 1, sm: 1.5 } }}>
                                            <Typography 
                                                variant="body2" 
                                                color="success.main" 
                                                fontWeight="bold"
                                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                            >
                                                {formatAmount(route.total_revenue)}
                                            </Typography>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Alert severity="info">
                    <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                    >
                        No Route Data
                    </Typography>
                    <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                        No route statistics available.
                    </Typography>
                </Alert>
            )}
        </CardContent>
    </Card>
    );
};



// Daily Bookings Chart
const DailyBookingsChart = ({ dailyData, loading }) => {
    const canViewRevenue = authService.canViewRevenue();
    
    return (
        <Card sx={{ height: '100%', minHeight: { xs: 250, sm: 300, md: 400 } }}>
            <CardHeader
                avatar={
                    <Avatar sx={{ bgcolor: 'primary.main', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                        <TrendingUpIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
                    </Avatar>
                }
                title={
                    <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' } }}>
                        Daily Bookings Trend
                    </Typography>
                }
                subheader={
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>
                        Last 30 days performance
                    </Typography>
                }
                sx={{ pb: 1, px: { xs: 1.5, sm: 2, md: 3 } }}
            />
            <CardContent sx={{ px: { xs: 1.5, sm: 2, md: 3 }, height: 'calc(100% - 80px)' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : dailyData?.length > 0 ? (
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Typography 
                            variant="h6" 
                            gutterBottom
                            sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' } }}
                        >
                            Recent Booking Activity
                        </Typography>
                        <Box sx={{ flex: 1, overflowY: 'auto' }}>
                            <Grid container spacing={{ xs: 1, sm: 2 }}>
                                {dailyData.slice(-7).map((item, index) => (
                                    <Grid item xs={12} key={index}>
                                        <Box sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'center', 
                                            py: { xs: 0.5, sm: 1 },
                                            flexDirection: { xs: 'column', sm: 'row' },
                                            gap: { xs: 1, sm: 2 }
                                        }}>
                                            <Typography 
                                                variant="body2"
                                                sx={{ 
                                                    fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                                                    fontWeight: 'medium'
                                                }}
                                            >
                                                {new Date(item.date).toLocaleDateString()}
                                            </Typography>
                                            <Box sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: { xs: 1, sm: 2 },
                                                flexDirection: { xs: 'column', sm: 'row' }
                                            }}>
                                                <Chip 
                                                    label={`${item.count} bookings`} 
                                                    size="small" 
                                                    color="primary"
                                                    sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' } }}
                                                />
                                                {canViewRevenue && (
                                                    <Typography 
                                                        variant="body2" 
                                                        color="success.main" 
                                                        fontWeight="bold"
                                                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}
                                                    >
                                                        {formatAmount(item.revenue)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        <Divider sx={{ my: { xs: 0.5, sm: 1 } }} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Box>
                ) : (
                    <Alert severity="info">
                        <Typography 
                            variant="h6" 
                            gutterBottom
                            sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' } }}
                        >
                            No Booking Data
                        </Typography>
                        <Typography 
                            variant="body2"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}
                        >
                            No daily booking data available.
                        </Typography>
                    </Alert>
                )}
            </CardContent>
        </Card>
    );
};

// Main Admin Dashboard Component
const AdminDashboard = () => {
    const { showError, showSuccess, showInfo } = useApiHandler();
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);
    
    // Date filter state
    const [dateRange, setDateRange] = useState({
        fromDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], // First day of current month
        toDate: new Date().toISOString().split('T')[0] // Today
    });
    const [presetRange, setPresetRange] = useState('custom');
    
    // Check user permissions
    const isAdmin = authService.isAdmin();
    const canViewRevenue = authService.canViewRevenue();

    // Load dashboard data
    const loadDashboardData = useCallback(async () => {
        setLoading(true);
        const response = await dashboardService.getDashboardStats(dateRange.fromDate, dateRange.toDate);
        if (response.success) {
            setDashboardData(response.data);
            setLastUpdated(new Date());
        } else {
            showError(response.message || 'Failed to perform operation');
        }
        setLoading(false);
    }, [showError, dateRange]);

    // Handle date range changes
    const handleDateRangeChange = (field, value) => {
        setDateRange(prev => ({
            ...prev,
            [field]: value
        }));
        setPresetRange('custom');
    };

    // Handle preset range selection
    const handlePresetRangeChange = (preset) => {
        setPresetRange(preset);
        const today = new Date();
        let fromDate, toDate;

        switch (preset) {
            case 'today':
                fromDate = toDate = today.toISOString().split('T')[0];
                break;
            case 'yesterday':
                const yesterday = new Date(today);
                yesterday.setDate(today.getDate() - 1);
                fromDate = toDate = yesterday.toISOString().split('T')[0];
                break;
            case 'last7days':
                const last7Days = new Date(today);
                last7Days.setDate(today.getDate() - 7);
                fromDate = last7Days.toISOString().split('T')[0];
                toDate = today.toISOString().split('T')[0];
                break;
            case 'last30days':
                const last30Days = new Date(today);
                last30Days.setDate(today.getDate() - 30);
                fromDate = last30Days.toISOString().split('T')[0];
                toDate = today.toISOString().split('T')[0];
                break;
            case 'thisMonth':
                fromDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
                toDate = today.toISOString().split('T')[0];
                break;
            case 'lastMonth':
                const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                fromDate = lastMonth.toISOString().split('T')[0];
                toDate = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split('T')[0];
                break;
            default:
                return;
        }

        setDateRange({ fromDate, toDate });
    };

    // Clear date filters
    const clearDateFilters = () => {
        const today = new Date();
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        setDateRange({
            fromDate: firstDayOfMonth.toISOString().split('T')[0],
            toDate: today.toISOString().split('T')[0]
        });
        setPresetRange('custom');
    };



    // Load all data
    const loadAllData = useCallback(async () => {
        await loadDashboardData();
    }, [loadDashboardData]);

    // Initial load
    useEffect(() => {
        loadAllData();
    }, [loadAllData]);

    // Auto-refresh dashboard data every 15 minutes
    useEffect(() => {
        const interval = setInterval(() => {
            loadDashboardData();
        }, 15 * 60 * 1000); // 15 minutes

        return () => clearInterval(interval);
    }, [loadDashboardData]);

    const overview = dashboardData?.overview || {};
    const performanceMetrics = dashboardData?.performance_metrics || {};

    return (
        <DashboardLayout title="Admin Dashboard" userType="employee">
            <Container maxWidth="xl" sx={{ 
                px: { xs: 1, sm: 2, md: 3 }
            }}>
                {/* Header */}
                <Box sx={{ mb: { xs: 2, sm: 4 } }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: { xs: 'flex-start', sm: 'center' }, 
                        justifyContent: 'space-between', 
                        mb: 2,
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 2, sm: 0 }
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ 
                                bgcolor: 'primary.main', 
                                mr: 2, 
                                width: { xs: 40, sm: 48 }, 
                                height: { xs: 40, sm: 48 }
                            }}>
                                <DashboardIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                            </Avatar>
                            <Box>
                                <Typography 
                                    variant="h4" 
                                    component="h1" 
                                    fontWeight="bold"
                                    sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
                                >
                                    Admin Dashboard
                                </Typography>
                                <Typography 
                                    variant="body1" 
                                    color="text.secondary"
                                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                >
                                    Real-time system overview and analytics
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: { xs: 1, sm: 2 },
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignSelf: { xs: 'stretch', sm: 'auto' }
                        }}>
                            {lastUpdated && (
                                <Typography 
                                    variant="caption" 
                                    color="text.secondary"
                                    sx={{ 
                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                        textAlign: { xs: 'center', sm: 'left' }
                                    }}
                                >
                                    Last updated: {lastUpdated.toLocaleTimeString()}
                                </Typography>
                            )}
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={loadAllData}
                                disabled={loading}
                                size="small"
                                sx={{ 
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Box>
                </Box>

                {/* Date Filter Section */}
                <Card sx={{ mb: 3, p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FilterIcon color="primary" />
                            <Typography variant="subtitle1" fontWeight="medium">
                                Date Range Filter
                            </Typography>
                        </Box>
                        
                        {/* Preset Range Selector */}
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Quick Select</InputLabel>
                            <Select
                                value={presetRange}
                                label="Quick Select"
                                onChange={(e) => handlePresetRangeChange(e.target.value)}
                            >
                                <MenuItem value="today">Today</MenuItem>
                                <MenuItem value="yesterday">Yesterday</MenuItem>
                                <MenuItem value="last7days">Last 7 Days</MenuItem>
                                <MenuItem value="last30days">Last 30 Days</MenuItem>
                                <MenuItem value="thisMonth">This Month</MenuItem>
                                <MenuItem value="lastMonth">Last Month</MenuItem>
                                <MenuItem value="custom">Custom Range</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Custom Date Range */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <TextField
                                label="From Date"
                                type="date"
                                value={dateRange.fromDate}
                                onChange={(e) => handleDateRangeChange('fromDate', e.target.value)}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                            <Typography variant="body2" color="text.secondary">
                                to
                            </Typography>
                            <TextField
                                label="To Date"
                                type="date"
                                value={dateRange.toDate}
                                onChange={(e) => handleDateRangeChange('toDate', e.target.value)}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Box>

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={loadDashboardData}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                            >
                                Apply Filter
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={clearDateFilters}
                                startIcon={<ClearIcon />}
                            >
                                Clear
                            </Button>
                        </Box>

                        {/* Current Range Display */}
                        <Box sx={{ ml: 'auto' }}>
                            <Chip
                                label={`${new Date(dateRange.fromDate).toLocaleDateString()} - ${new Date(dateRange.toDate).toLocaleDateString()}`}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        </Box>
                    </Box>
                </Card>

                {/* First Part: Stat Cards Grid */}
                <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ mb: 3 }}>
                    {/* Key Metrics Row - Responsive Grid */}
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                        <Fade in={true} timeout={300}>
                            <div>
                                <StatCard
                                    title="Total Bookings"
                                    value={overview.total_bookings?.toLocaleString() || '0'}
                                    icon={<ShippingIcon />}
                                    color="#1976d2"
                                    loading={loading}
                                    growth={overview.booking_growth}
                                    subtitle="All time bookings"
                                    gradient={true}
                                />
                            </div>
                        </Fade>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                        <Fade in={true} timeout={400}>
                            <div>
                                <StatCard
                                    title="Total Customers"
                                    value={overview.total_customers?.toLocaleString() || '0'}
                                    icon={<PeopleIcon />}
                                    color="#2e7d32"
                                    loading={loading}
                                    subtitle="Registered customers"
                                    gradient={true}
                                />
                            </div>
                        </Fade>
                    </Grid>
                    
                    {/* Monthly Revenue - Admin Only */}
                    {canViewRevenue && (
                        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                            <Fade in={true} timeout={500}>
                                <div>
                                    <StatCard
                                        title="Monthly Revenue"
                                        value={formatAmount(overview.monthly_revenue)}
                                        icon={<MoneyIcon />}
                                        color="#ed6c02"
                                        loading={loading}
                                        growth={overview.revenue_growth}
                                        subtitle="This month earnings"
                                        gradient={true}
                                    />
                                </div>
                            </Fade>
                        </Grid>
                    )}
                    
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                        <Fade in={true} timeout={600}>
                            <div>
                                <StatCard
                                    title="Today's Bookings"
                                    value={overview.today_bookings?.toLocaleString() || '0'}
                                    icon={<TodayIcon />}
                                    color="#d32f2f"
                                    loading={loading}
                                    subtitle={canViewRevenue ? `Revenue: ${formatAmount(overview.today_revenue)}` : "Today's activity"}
                                    gradient={true}
                                />
                            </div>
                        </Fade>
                    </Grid>

                    {/* Performance Metrics Row - Responsive Grid */}
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                        <Slide direction="up" in={true} timeout={700}>
                            <div>
                                <StatCard
                                    title="Delivery Rate"
                                    value={`${performanceMetrics.delivery_rate || 0}%`}
                                    icon={<DeliveryIcon />}
                                    color="#4caf50"
                                    loading={loading}
                                    subtitle="Successful deliveries"
                                />
                            </div>
                        </Slide>
                    </Grid>
                    
                    {/* Avg Booking Value - Admin Only */}
                    {canViewRevenue && (
                        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                            <Slide direction="up" in={true} timeout={800}>
                                <div>
                                    <StatCard
                                        title="Avg Booking Value"
                                        value={formatAmount(performanceMetrics.average_booking_value)}
                                        icon={<AnalyticsIcon />}
                                        color="#9c27b0"
                                        loading={loading}
                                        subtitle="Per shipment average"
                                    />
                                </div>
                            </Slide>
                        </Grid>
                    )}
                    
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                        <Slide direction="up" in={true} timeout={900}>
                            <div>
                                <StatCard
                                    title="Pending"
                                    value={overview.pending_count?.toLocaleString() || '0'}
                                    icon={<PendingIcon />}
                                    color="#ff9800"
                                    loading={loading}
                                    subtitle="Awaiting processing"
                                />
                            </div>
                        </Slide>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                        <Slide direction="up" in={true} timeout={1000}>
                            <div>
                                <StatCard
                                    title="In Transit"
                                    value={overview.in_transit_count?.toLocaleString() || '0'}
                                    icon={<FlightTakeoffIcon />}
                                    color="#2196f3"
                                    loading={loading}
                                    subtitle="Currently shipping"
                                />
                            </div>
                        </Slide>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                        <Slide direction="up" in={true} timeout={1100}>
                            <div>
                                <StatCard
                                    title="Out for Delivery"
                                    value={overview.out_for_delivery_count?.toLocaleString() || '0'}
                                    icon={<LocalShippingIcon />}
                                    color="#9c27b0"
                                    loading={loading}
                                    subtitle="On delivery route"
                                />
                            </div>
                        </Slide>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                        <Slide direction="up" in={true} timeout={1200}>
                            <div>
                                <StatCard
                                    title="Delivered"
                                    value={overview.delivered_count?.toLocaleString() || '0'}
                                    icon={<DoneIcon />}
                                    color="#4caf50"
                                    loading={loading}
                                    subtitle="Successfully delivered"
                                />
                            </div>
                        </Slide>
                    </Grid>
                </Grid>

                {/* Second Part: Large Widgets Grid */}
                <Grid container spacing={{ xs: 2, sm: 3, md: 3 }}>
                    {/* Charts and Widgets Row - Equal Split */}
                    <Grid item xs={12} md={6} lg={6}>
                        <Zoom in={true} timeout={1300}>
                            <div>
                                <DailyBookingsChart 
                                    dailyData={dashboardData?.daily_bookings} 
                                    loading={loading} 
                                />
                            </div>
                        </Zoom>
                    </Grid>
                    
                    <Grid item xs={12} md={6} lg={6}>
                        <Zoom in={true} timeout={1400}>
                            <div>
                                <TopRoutesWidget 
                                    routes={dashboardData?.top_routes} 
                                    loading={loading} 
                                />
                            </div>
                        </Zoom>
                    </Grid>
                </Grid>
            </Container>
        </DashboardLayout>
    );
};

export default AdminDashboard; 